import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MenuService } from '@core/components/menu/services/menu.service';
import { AppService } from '@core/services/app.service';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { MenuItem } from '@shared/model/menu-item.type';
import { IconComponent } from '@ui/icon/icon.component';
import { filter, takeUntil } from 'rxjs';
import { BaseComponent } from '../base-component';
import { PendingValidationsBadgeService } from '@shared/services/pending-validations-badge.service';
import { AppRoutes } from '@core/routes/route.paths';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgbPopover, RouterLink, RouterLinkActive, TranslatePipe, NgTemplateOutlet, IconComponent],
})
export class MenuComponent extends BaseComponent implements OnInit, OnDestroy {
  private readonly appService = inject(AppService);
  public readonly menuService = inject(MenuService);
  private readonly router = inject(Router);
  private readonly pendingBadgeService = inject(PendingValidationsBadgeService);

  protected activeUrl: string = this.router.url;
  protected visibleMenus: MenuItem[] = [];
  protected pendingValidationsCount = this.pendingBadgeService.count;
  protected readonly validerPlisRoute = AppRoutes.ValiderPlis;

  constructor() {
    super();
    this.visibleMenus = this.menuService.getMenu().filter((menu) => !menu.show || menu.show());
    this.initSubmenu();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.activeUrl = this.router.url;
        this.syncSubmenuWithUrl();
      });
  }

  ngOnInit(): void {
    this.pendingBadgeService.refresh();
  }

  private initSubmenu() {
    const menu = this.findMenuForUrl();
    if (menu) {
      this.setSubmenu(menu);
    }
  }

  private syncSubmenuWithUrl() {
    const menu = this.findMenuForUrl();
    this.menuService.subMenu.set(menu?.subMenu ?? null);
  }

  private findMenuForUrl(): MenuItem | undefined {
    const url = this.router.url;
    return this.visibleMenus.find(
      (m) =>
        !!m.subMenu &&
        (url.startsWith(m.routerLink) || m.subMenu.items.some((item) => url.startsWith(item.routerLink))),
    );
  }

  protected setSubmenu(menu: MenuItem) {
    const isSameRoute = this.router.url.startsWith(menu.routerLink);
    this.menuService.subMenu.set(menu.subMenu);
    if (isSameRoute) {
      this.menuService.toggleMenuAndLabel();
    } else {
      this.menuService.displaySubMenuAndMenuLabel();
    }
  }

  protected urlIsActive(url: string) {
    return this.activeUrl.startsWith(`/${url}`);
  }

  openSubMenu(subMenuName: string) {
    if (`/${subMenuName}` !== this.activeUrl) {
      if (subMenuName.includes('validator')) {
        this.router.navigateByUrl(this.appService.uploadLink());
      } else if (!this.urlIsActive(subMenuName)) {
        this.router.navigateByUrl(`/${subMenuName}`);
      }
      this.menuService.setShowMenuTrue();
    } else {
      this.menuService.updateShowMenu();
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.menuService.subMenu.set(null);
  }
}
